import React from "react";
import { Link, useParams } from "react-router-dom";
import { useGetAdminWorkflowExecutionQuery } from "@/store/admin_api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FaArrowLeft } from "react-icons/fa";
import { formatDateTime } from "@/util/date";
import { prettyJson } from "@/util/json";

const RUN_STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  RUNNING: "default",
  COMPLETED: "secondary",
  FAILED: "destructive",
  CANCELLED: "outline",
};

const STEP_STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  PENDING: "outline",
  RUNNING: "default",
  WAITING: "default",
  COMPLETED: "secondary",
  FAILED: "destructive",
  SKIPPED: "outline",
};

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b py-3 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{children}</span>
    </div>
  );
}

export default function WorkflowRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: run, isLoading } = useGetAdminWorkflowExecutionQuery(id ?? "", { skip: !id });

  if (isLoading || !run) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const contactName = [run.contact?.firstName, run.contact?.lastName].filter(Boolean).join(" ");

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
          <Link to="/platform-admin/activity?tab=workflow-runs">
            <FaArrowLeft className="mr-2" />
            All workflow runs
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-bold">{run.workflow?.name ?? "Deleted workflow"}</h2>
          <Badge variant={RUN_STATUS_VARIANTS[run.status] ?? "outline"}>{run.status}</Badge>
        </div>
        <p className="font-mono text-sm text-muted-foreground">{run.id}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Run</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="Organisation">{run.workflow?.organisation?.name ?? "Unknown"}</DetailRow>
          <DetailRow label="Trigger">{run.workflow?.triggerType ?? "Unknown"}</DetailRow>
          <DetailRow label="Triggered">{formatDateTime(run.triggeredAt)}</DetailRow>
          <DetailRow label="Completed">{formatDateTime(run.completedAt)}</DetailRow>
          {contactName && <DetailRow label="Contact">{contactName}</DetailRow>}
          {run.error && (
            <DetailRow label="Error">
              <span className="text-destructive">{run.error}</span>
            </DetailRow>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
          <CardDescription>Every step in this run, in the order it was scheduled.</CardDescription>
        </CardHeader>
        <CardContent>
          {!run.steps || run.steps.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">This run has no steps.</p>
          ) : (
            <div className="divide-y rounded-lg border">
              {run.steps.map((step, index) => (
                <div key={step.id} className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">
                        <span className="mr-2 text-muted-foreground tabular-nums">{index + 1}.</span>
                        {step.actionType}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Scheduled {formatDateTime(step.scheduledAt)}
                        {step.executedAt ? `, ran ${formatDateTime(step.executedAt)}` : ""}
                        {step.retryCount > 0 ? `, ${step.retryCount} retries` : ""}
                      </p>
                    </div>
                    <Badge variant={STEP_STATUS_VARIANTS[step.status] ?? "outline"} className="shrink-0">
                      {step.status}
                    </Badge>
                  </div>

                  {step.outcome && <p className="text-sm">{step.outcome}</p>}
                  {step.error && <p className="text-sm text-destructive">{step.error}</p>}

                  {step.config && step.config !== "{}" && (
                    <pre className="whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-xs">
                      {prettyJson(step.config)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
